import { useEffect, useRef } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type { Geometry } from 'geojson';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useIncidentAreas } from 'components/IAP/hooks/incidentAreaHooks';
import type { ArcgisMapView } from './arcgisView';

export const INCIDENT_AREA_LAYER_ID = 'ics-215a-incident-areas';
export const INCIDENT_AREA_LAYER_TITLE = '215A Incident Areas';

/** Amber, so a hazard reads as one against the operational symbology over it. */
const AREA_RGB = [255, 176, 46] as const;

const [R, G, B] = AREA_RGB;

const POINT_SYMBOL: __esri.SimpleMarkerSymbolProperties & {
  type: 'simple-marker';
} = {
  type: 'simple-marker',
  style: 'circle',
  color: [R, G, B, 1],
  size: 12,
  outline: { color: [90, 54, 0, 1], width: 1.5 },
};

const POLYGON_SYMBOL: __esri.SimpleFillSymbolProperties & {
  type: 'simple-fill';
} = {
  type: 'simple-fill',
  color: [R, G, B, 0.25],
  outline: { color: [R, G, B, 1], width: 2 },
};

/**
 * A `GraphicsLayer` rather than a `GeoJSONLayer`, because an area may be a
 * dropped pin or a drawn outline and a GeoJSONLayer takes its geometry type
 * from the first feature it reads.
 */
const toEsriGeometry = (geometry: Geometry): __esri.Geometry | null => {
  if (geometry.type === 'Point') {
    const [longitude, latitude] = geometry.coordinates;
    return new Point({ longitude, latitude });
  }
  if (geometry.type === 'Polygon') {
    return new Polygon({ rings: geometry.coordinates });
  }
  if (geometry.type === 'MultiPolygon') {
    return new Polygon({ rings: geometry.coordinates.flat() });
  }
  return null;
};

const POPUP_TEMPLATE: __esri.PopupTemplateProperties = {
  title: '{name}',
  content: [
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'hazard', label: 'Hazards / Risks' },
        { fieldName: 'mitigations', label: 'Mitigations' },
        { fieldName: 'riskGain', label: 'Risk/Gain' },
      ],
    },
  ],
};

/**
 * Manages the lifecycle of the "215A Incident Areas" layer.
 *
 * The areas are boxes 5-8 of the ICS 215A, each with an optional shape someone
 * drew on the safety analysis card. Like the AOR and action layers this is
 * built on the client rather than seeded as a row in `internal_layers`, which
 * is what makes it present in every incident workspace without anything having
 * to create it when the incident is created.
 */
export function useIncidentAreaLayerSync({
  mapView,
  isVisible,
}: {
  mapView: ArcgisMapView | null;
  isVisible: boolean;
}) {
  const incidentId = useActualIncidentId();
  const { data: areas = [] } = useIncidentAreas(incidentId);

  const layerRef = useRef<GraphicsLayer | null>(null);

  // The layer exists for as long as the map does, so toggling it off is a
  // visibility change rather than a teardown.
  useEffect(() => {
    if (!mapView?.map) return;

    const map = mapView.map;
    const layer = new GraphicsLayer({
      id: INCIDENT_AREA_LAYER_ID,
      title: INCIDENT_AREA_LAYER_TITLE,
      visible: isVisible,
    });
    map.add(layer);
    layerRef.current = layer;

    return () => {
      map.remove(layer);
      layer.destroy();
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapView]);

  useEffect(() => {
    const layer = layerRef.current;
    if (layer) layer.visible = isVisible;
  }, [isVisible]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    layer.removeAll();

    for (const area of areas) {
      // Most areas are typed in without ever being drawn, and a graphic needs
      // a shape to exist on the map.
      if (!area.geometry) continue;
      const geometry = toEsriGeometry(area.geometry);
      if (!geometry) continue;

      layer.add(
        new Graphic({
          geometry,
          symbol:
            area.geometry.type === 'Point' ? POINT_SYMBOL : POLYGON_SYMBOL,
          attributes: {
            areaId: area.areaId,
            name: area.areaName,
            hazard: area.hazard,
            mitigations: area.mitigations,
            riskGain: area.riskGain,
          },
          popupTemplate: POPUP_TEMPLATE,
        })
      );
    }
  }, [areas, mapView]);
}
